import { computed } from 'vue'

import { ASSET_FIELD } from '@/const'
import { TAG_FIELD } from '@/entities/tags/utils/const'
import { TRANSACTION_TYPE } from '@/entities/transactions/utils/const'

import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useRepositoriesStore } from '@/store/repositories'
import { useTagsBunchStore } from '@/store/tags/bunch'
import { useTransactionsSettingsStore } from '@/store/transactions/settings'

import { orderBy } from 'lodash'
import { Tag } from '@types'

/**
 * The portfolio facts every AI prompt in this app is rendered against: which
 * accounts exist, which currencies, which tags are already in use, and which
 * account types each transaction type accepts.
 *
 * Shared rather than per-store because the prompts quote them in the same
 * formats — a divergence here would show up as a model that recommends an
 * account type the repository does not have.
 */
export const useAIPortfolioContext = () => {
  const assetsBunchStore = useAssetsBunchStore()
  const repositoriesStore = useRepositoriesStore()
  const tagsBunchStore = useTagsBunchStore()
  const transactionsSettingsStore = useTransactionsSettingsStore()

  const settingsList = computed(() => transactionsSettingsStore.getList)

  /**
   * The account types a transaction accepts, from its own settings entry —
   * sorted by name, the same order every dropdown built from this list (and
   * from `accountsList`/`currenciesList` below) is expected to read in.
   *
   * A plain read rather than a computed: a computed per call would allocate one
   * on every evaluation of whatever computed called it. Callers that need
   * reactivity wrap this — `allAccountTypes` below does, and so does the wizard.
   */
  const accountTypesFor = (transactionType: string): string[] =>
    orderBy(
      settingsList.value.find(item => item.name === transactionType)?.entries[0].account
        ?.includes ?? [],
      type => type.toLowerCase(),
    )

  /**
   * The asset types a transaction accepts, from the same settings entry
   * `accountTypesFor` reads — the repository's own controlled vocabulary, not
   * a value the wizard or the model may invent. private_security is the one
   * flow that lets the user (or the model) name the type at all; every other
   * flow fixes it in the wizard's own definition instead. Sorted by name, same
   * as `accountTypesFor`.
   */
  const assetTypesFor = (transactionType: string): string[] =>
    orderBy(
      settingsList.value.find(item => item.name === transactionType)?.entries[0].asset
        ?.includes ?? [],
      type => type.toLowerCase(),
    )

  /**
   * Every account type any transaction this app creates accepts — the union
   * across every named `TRANSACTION_TYPE`, not just the two most flows use.
   * private_fund_subscribe_and_commit's own investors and private_security's
   * own entities post `subscribe and commit` / `subscribe and fund`, and each
   * of those has its own settings entry that can, in principle, list account
   * types the `add position` / `add liability` entries do not — so a type
   * valid only for one of those two flows still has to appear here, or the
   * classifier would reject it as unknown the moment a sentence named it.
   *
   * Sorted by name — the same order every dropdown built from this list is
   * expected to read in, and the order the classifier is quoted the list in.
   */
  const allAccountTypes = computed(() =>
    orderBy(
      [...new Set(Object.values(TRANSACTION_TYPE).flatMap(type => accountTypesFor(type)))],
      type => type.toLowerCase(),
    ),
  )

  /**
   * Accounts the model may pick from, quoted the way the prompts read them.
   *
   * The id is included because an answer can name one: the asset wizard resolves
   * the account a record hangs off against this list, and an id is what
   * separates "link to the trust you already have" from "create a second one
   * with the same name". A prompt that has no use for it simply does not
   * mention it — the same list serves all of them, which is the point of this
   * file.
   */
  const accountsList = computed(() => {
    const accounts = []
    for (const asset of assetsBunchStore.getList.values()) {
      const type = asset.field<string>(ASSET_FIELD.TYPE).value
      if (!allAccountTypes.value.includes(type)) continue
      const name = asset.field<string>(ASSET_FIELD.NAME).value
      const ticker = asset.field<string>(ASSET_FIELD.TICKER).value
      accounts.push({ id: asset.id, name, type, ticker })
    }
    return JSON.stringify(accounts)
  })

  const currenciesList = computed(() =>
    orderBy(repositoriesStore.getCurrencies.map(item => item.ticker)),
  )

  /**
   * Tag pairs already in use, so a model prefers them over inventing new ones.
   * Deduped: a tag name/value pair is one dimension of the repository however
   * many assets carry it.
   *
   * The store's common list, so Category and Subcategory are never quoted: they
   * are the app's own category tree, maintained by the income and tagging-rule
   * screens, and a model shown them suggests them — which is a tag the user
   * cannot edit by hand once it lands. Both readers of this are wizard
   * assistants (the flow read and the tag suggester), and neither has any
   * business filing an asset under that tree.
   */
  const tagsList = computed(() => {
    const pairs = new Set<Pick<Tag, 'id' | 'tag_name' | 'tag_value'>>()
    for (const tag of tagsBunchStore.getCommonList.values()) {
      const name = tag.field<string>(TAG_FIELD.TAG_NAME).value
      const value = tag.field<string>(TAG_FIELD.TAG_VALUE).value
      if (!name || !value) continue
      pairs.add({ id: tag.id, tag_name: name, tag_value: value })
    }
    return JSON.stringify(Array.from(pairs))
  })

  return {
    accountsList,
    currenciesList,
    tagsList,
    /*
     * Which account types a given transaction accepts. One factory rather than
     * an accessor per transaction: the four it replaced were partial
     * applications of it, and a caller that needs a fifth transaction — the
     * asset wizards ask  about whichever one their flow posts — could not be
     * served by a fixed set at all.
     */
    accountTypesFor,
    assetTypesFor,
    allAccountTypes,
  }
}
